import { useEffect, useState } from 'react';

import { calcItemPrice, cartStore } from '../../store/cartStore';
import styles from './CartItem.module.scss';

import type { CartItemModel } from '@/entities/cart/model/CartItemModel';
import { ProductImage } from '@/entities/product/ui/ProductImage/ProductImage';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { formatPrice } from '@/shared/lib/currency';
import { observer } from '@/shared/lib/signal/observer';
import { AddToCartButton } from '@/shared/ui-kit/AddToCartButton/AddToCartButton';
import { useToast } from '@/shared/ui-kit/ToastProvider/useToast';

type Props = { item: CartItemModel };

const CartItemBase = observer(({ item }: Props) => {
  const productState = cartStore.getProduct(item);
  const { showError } = useToast();

  const product = productState.value;
  if (!product) return null;

  const unitPrice = calcItemPrice({
    productId: item.productId,
    menuId: item.menu,
    qty: item.quantity,
    options: item.productOptions,
  });

  const [qty, setQty] = useState<number>(item.quantity ?? 1);

  useEffect(() => setQty(item.quantity ?? 1), [item.quantity]);
  const debouncedUpdate = useDebounce((newQty: number) => {
    const payload = {
      id: item.id,
      quantity: newQty,
      productOptions: (item.productOptions ?? []).map((opt) => ({
        ...opt,
        quantity: newQty,
      })),
    };

    cartStore
      .updateQty(payload)
      .catch(() => showError('Ошибка изменения количества товара'));
  }, 300);

  const handleDecrease = (next: number) => {
    debouncedUpdate(next);
  };

  const handleIncrease = (next: number) => {
    debouncedUpdate(next);
  };

  return (
    <>
      <li className={styles.row}>
        <ProductImage className={styles.image} src={product.image} />
        <div className={styles.rightCol}>
          <div className={styles.productInfo}>
            <div className={styles.title}>{product.name}</div>
            <div className={styles.meta}>{product.parameter}</div>

            {item.productOptions.map((opt) => (
              <div key={opt.optionId} className={styles.optionItem}>
                <h4 className={styles.option}>
                  {opt.optionName}{' '}
                  <span className={styles.optionPrice}>
                    +{formatPrice(opt.price * opt.quantity)}
                  </span>
                </h4>
              </div>
            ))}

            <div>
              <AddToCartButton
                size="S"
                min={0}
                value={qty}
                onDecrease={handleDecrease}
                onIncrease={handleIncrease}
                className={styles.AddToCartButton}
              />
            </div>
          </div>

          <div className={styles.sum}>{formatPrice(unitPrice)}</div>
        </div>
      </li>
    </>
  );
});

export const CartItem = observer(CartItemBase);
